import { Injectable } from "@angular/core";
import { BehaviorSubject, fromEvent, map, Observable } from "rxjs";

import { UrlUtils } from "../../../shared-library/utils/url.utils";

import { SavedSearchModel } from "./saved-search.model";

@Injectable({
    providedIn: "root"
})
export class SavedSearchesService {
    public savedSearches$: BehaviorSubject<Array<SavedSearchModel>>;
    public isSearchSaved$: Observable<{ saved: boolean, }>;
    private LOCAL_STORAGE_KEY = "savedSearches";

    public constructor() {
        const serialized = window.localStorage.getItem(this.LOCAL_STORAGE_KEY);
        const savedSearches = serialized ? JSON.parse(serialized) as Array<SavedSearchModel> : [];
        this.savedSearches$ = new BehaviorSubject<Array<SavedSearchModel>>(savedSearches);

        fromEvent(window, "pcg.querystringchanged")
            .subscribe(() => this.savedSearches$.next([...this.savedSearches$.value]));

        this.isSearchSaved$ = this.savedSearches$.pipe(
            map((currentItems) => {
                const currentUrl = UrlUtils.normalizeUrl(window.location.href);
                return { saved: currentItems.some(s => UrlUtils.normalizeUrl(s.url) === currentUrl) };
            })
        );

        this.savedSearches$.subscribe(this.saveToLocalStorage);
    }

    public create(savedSearch: SavedSearchModel): void {
        savedSearch.id = this.generateId();
        const currentValues = this.savedSearches$.value;
        currentValues.push(savedSearch);

        this.savedSearches$.next(currentValues);
    }

    public update(savedSearch: SavedSearchModel): void {
        const currentValues = this.savedSearches$.value;
        const updatedValue = currentValues.find(s => s.id === savedSearch.id);
        if (!updatedValue) {
            this.create(savedSearch);
            return;
        }

        updatedValue.name = savedSearch.name;
        updatedValue.url = savedSearch.url;
        updatedValue.description = savedSearch.description;

        this.savedSearches$.next(currentValues);
    }

    public delete(savedSearch: SavedSearchModel): void {
        let currentValues = this.savedSearches$.value;
        currentValues = currentValues.filter(s => s.id !== savedSearch.id);

        this.savedSearches$.next(currentValues);
    }

    private generateId: () => string = () =>
        Date.now().toString(36) + Math.random().toString(36).substr(2);

    private saveToLocalStorage: (currentItems: Array<SavedSearchModel>) => void
        = (currentItems: Array<SavedSearchModel>) => window.localStorage.setItem(this.LOCAL_STORAGE_KEY, JSON.stringify(currentItems));
}
